/**
 * CRM-QUOTE-PIPELINE — pure value-rollup helpers for the Quote Pipeline
 * lane header.
 *
 * A quote board is a funnel: each lane (Open → Quoting → Submitted →
 * Awarded → Lost) should show, at a glance, how much money is sitting in
 * that stage and the RISK-WEIGHTED figure (Σ value × win%). These are pure
 * I→O transforms so the rollup is unit-tested without mounting the lane.
 *
 *   computeQuoteLaneRollup(cards) → { gross, weighted, valued } | null
 *   formatCompactMoney(cents)     → "$1.9M" | "$185k" | "$18.5k" | "$950"
 *
 * `gross`/`weighted` are in CENTS (the card's native unit); `win_pct` is
 * clamped to 0–100 defensively in case a bad feed pushes an out-of-range
 * value. A lane with no valued cards returns null so the header omits the
 * chip rather than rendering "$0".
 */

import type { Card } from '../types'

export interface QuoteLaneRollup {
  /** Σ amount_cents over cards that carry a positive value. */
  gross: number
  /** Σ amount_cents × clamp(win_pct, 0, 100) / 100 (risk-weighted). */
  weighted: number
  /** Count of cards contributing a positive value. */
  valued: number
}

/** True for the five Quote Pipeline lane kinds (QUOTE_OPEN … QUOTE_LOST). */
export function isQuoteLaneKind(kind: string): boolean {
  return kind.startsWith('QUOTE_')
}

/** Roll a lane's cards into gross + win-weighted value. Null when no card
 *  carries a positive amount (so the header shows nothing, not "$0"). */
export function computeQuoteLaneRollup(cards: readonly Card[]): QuoteLaneRollup | null {
  let gross = 0
  let weighted = 0
  let valued = 0
  for (const c of cards) {
    const cents =
      typeof c.amount_cents === 'number' && c.amount_cents > 0 ? c.amount_cents : 0
    if (!cents) continue
    gross += cents
    valued += 1
    const raw = typeof c.win_pct === 'number' ? c.win_pct : 0
    const win = raw <= 0 ? 0 : raw >= 100 ? 100 : raw
    if (win > 0) weighted += Math.round((cents * win) / 100)
  }
  if (gross <= 0) return null
  return { gross, weighted, valued }
}

/** Compact money from CENTS: "$1.9M" / "$185k" / "$18.5k" / "$950". */
export function formatCompactMoney(cents: number): string {
  const dollars = cents / 100
  const abs = Math.abs(dollars)
  if (abs >= 1_000_000) {
    return `$${(dollars / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`
  }
  if (abs >= 1_000) {
    return `$${(dollars / 1_000).toFixed(abs >= 100_000 ? 0 : 1)}k`
  }
  return `$${Math.round(dollars)}`
}

/** Full money from CENTS: "$162,610.01" / "$0.00". The card's HEADLINE quote
 *  figure shows the exact amount (the compact form above is for the lane-header
 *  rollup). Deterministic 'en-US' grouping so tests don't depend on host
 *  locale; AU/US both render comma-thousands + period-decimal. */
export function formatFullMoney(cents: number): string {
  const dollars = cents / 100
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}
